import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { enumBondType } from '@data-layer/enums/common/enumBondType';
import {
  BOND_REFERENCE_NUMBER_MALFORMED,
  BOND_REQUEST_AMOUNT_MISSING,
  BOND_REQUEST_PORT_CODE_MISSING,
  BOND_SURETY_CODE_MISSING,
  BOND_TYPE_MISSING,
  SINGLE_BOND_REQUEST_FORBIDDEN,
} from '@constants/entity-validation-messages';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { BondModel, BondRequestModel, PortModel, ValidationMessageModel } from '@data-layer/models/common';
import { CodeRepository } from '@data-layer/repositories';
import { enumBondRequestState } from '@data-layer/enums/common/enumBondRequestState';
import { EntityModel } from '@data-layer/models/entities';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { take } from 'rxjs/operators';
import { CustomState } from '@data-layer/enums/common';

@Component({
  selector: 'es-details-bond-card',
  templateUrl: './es-details-bond-card.component.html',
  styleUrls: ['./es-details-bond-card.component.scss'],
})
export class EsDetailsBondCardComponent implements OnInit, OnChanges {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public canEdit: boolean = false;
  @Input() public isShowBR: boolean;
  @Output() public edit: EventEmitter<void> = new EventEmitter<void>();
  public info = {
    title: 'LABELS.CUSTOMS_BOND',
    imgSrc: 'bond',
  };
  public isEmpty: boolean = false;
  public bondTypeLabel: string = '';
  public isSingleBondType: boolean = false;
  public allValidationMessages: ValidationMessageModel[] = [];
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public arrivalPort: PortModel;
  public enumBondRequestState: typeof enumBondRequestState = enumBondRequestState;

  private readonly missingFields: string[] = [
    BOND_SURETY_CODE_MISSING,
    BOND_TYPE_MISSING,
    BOND_REQUEST_PORT_CODE_MISSING,
    BOND_REQUEST_AMOUNT_MISSING,
    SINGLE_BOND_REQUEST_FORBIDDEN,
    BOND_REFERENCE_NUMBER_MALFORMED,
  ];

  constructor(private codeRepository: CodeRepository) {}

  public ngOnInit(): void {
    this.updateBondTypeLabel();
    this.checkIsMissing();
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.entrySummary) {
      this.updateBondTypeLabel();
      this.checkIsMissing();
      this.updatePort();
    }
  }

  public goToEdit(): void {
    this.edit.emit();
  }

  public get bondInfo(): BondModel {
    if (!this.entrySummary) {
      return;
    }

    if (!!this.entrySummary?.bondRequest && !!this.entrySummary?.bondInfo) {
      return this.checkBondInfo(this.entrySummary.bondInfo) ? null : this.entrySummary.bondInfo;
    } else if (!this.entrySummary?.bondRequest && !!this.entrySummary?.bondInfo) {
      return this.entrySummary.bondInfo;
    }
  }

  public get singleBondRequest(): BondRequestModel {
    return this.entrySummary &&
      this.entrySummary.bondRequest &&
      this.entrySummary.bondRequest.bondType === enumBondType.SINGLE_TRANSACTION &&
      this.entrySummary.bondRequest.state !== enumBondRequestState.COMPLETED
      ? this.entrySummary.bondRequest
      : null;
  }

  public get continuousBondRequest(): BondRequestModel {
    return this.entrySummary &&
      this.entrySummary.bondRequest &&
      this.entrySummary.bondRequest.bondType === enumBondType.CONTINUOUS &&
      this.entrySummary.bondRequest.state !== enumBondRequestState.COMPLETED
      ? this.entrySummary.bondRequest
      : null;
  }

  public get hasCanceledBondRequest(): boolean {
    return (
      this.entrySummary && this.entrySummary.bondRequest && this.entrySummary.bondRequest?.customState?.header === CustomState.CANCELED
    );
  }

  public get importer(): EntityModel {
    return this.entrySummary && this.entrySummary.shipments && this.entrySummary.shipments.length
      ? this.entrySummary.shipments[0].importer
      : null;
  }

  public get bondRequestNotSentError(): string {
    const message: ValidationMessageModel = this.entrySummary.validationMessages.length
      ? this.entrySummary.validationMessages.find((item: ValidationMessageModel) => item.text === 'BOND_REQUEST_NOT_SENT')
      : null;
    return message ? 'BACK_ERROR.' + message.text : null;
  }

  public get singleBondText(): string {
    if (!this.entrySummary?.bondRequest?.customState?.header) {
      return 'INFO.BN.SINGLE_BOND_PURCHASE_REQUESTED';
    }

    switch (this.entrySummary?.bondRequest?.customState?.header) {
      case CustomState.CANCELED:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_CANCELED';
      case CustomState.CANCELING:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_CANCELING';
      case CustomState.REFUNDED:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_REFUNDED';
      default:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_REQUESTED';
    }
  }

  private updateBondTypeLabel(): void {
    if (!this.bondInfo) {
      this.bondTypeLabel = '';
      return;
    }
    this.isSingleBondType = this.bondInfo.type === enumBondType.SINGLE_TRANSACTION;
    this.bondTypeLabel =
      this.bondInfo.type === enumBondType.SINGLE_TRANSACTION
        ? 'FORM_LABEL.CUSTOM_BOND_SINGLE'
        : this.bondInfo.type === enumBondType.CONTINUOUS
        ? 'FORM_LABEL.CUSTOM_BOND_CONTINUES'
        : '';
  }

  private checkIsMissing(): void {
    if (!this.bondInfo && !this.singleBondRequest && !this.continuousBondRequest) {
      this.isEmpty = true;
      return;
    }

    const allValidationMessages: ValidationMessageModel[] = EntrySummaryValidationHelper.getValidationMessages(
      this.entrySummary,
      this.entrySummary.transportationMode,
      true,
    );
    this.allValidationMessages = [];

    for (const mess of allValidationMessages) {
      if (this.missingFields.includes(mess.text)) {
        this.allValidationMessages.push(mess);
      }
    }

    if (this.bondInfo) {
      this.isEmpty = this.checkBondInfo(this.bondInfo);
    }
  }

  private checkBondInfo(bondInfo: BondModel): boolean {
    let res: boolean = true;
    const requiredFields: string[] = ['suretyCode', 'type', 'bondAmount', 'referenceNumber'];

    for (const field of requiredFields) {
      if (![null, undefined, ''].includes(bondInfo[field])) {
        res = false;
      }
    }

    return res;
  }

  private updatePort(): void {
    if (this.singleBondRequest && this.singleBondRequest.portCode) {
      this.codeRepository
        .getDomesticPort(this.singleBondRequest.portCode)
        .pipe(take(1))
        .subscribe((port: PortModel) => {
          this.arrivalPort = port;
        });
    }
  }
}
